export const paymentMethodLabelMap = Object.freeze({
    bank: 'تحويل بنكي',
    bank_transfer: 'تحويل بنكي',
    cash: 'نقد',
    pos: 'نقاط بيع',
    supplier_balance: 'رصيد مورد',
});
// These are the only item types accepted by the canonical visit RPC. Keep this
// list here so the editor never offers a type the database cannot persist.
export const CANONICAL_VISIT_ITEM_TYPES = Object.freeze(['service', 'part', 'supplier']);
export const isCanonicalVisitItemType = (value) => CANONICAL_VISIT_ITEM_TYPES
    .includes(String(value ?? '').trim().toLowerCase());
const asNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};
const incrementDecimalDigits = (digits) => {
    const next = digits.split('');
    for (let index = next.length - 1; index >= 0; index -= 1) {
        if (next[index] !== '9') {
            next[index] = String(Number(next[index]) + 1);
            return next.join('');
        }
        next[index] = '0';
    }
    return `1${next.join('')}`;
};
const decimalParts = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw || !Number.isFinite(Number(raw)))
        return null;
    const match = raw.match(/^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:e([+-]?\d+))?$/i);
    if (!match)
        return null;
    const sign = match[1] === '-' ? -1 : 1;
    const whole = match[2] || '0';
    const fraction = match[3] ?? match[4] ?? '';
    const exponent = Number(match[5] || 0);
    if (!Number.isSafeInteger(exponent))
        return null;
    const combined = `${whole}${fraction}`;
    const firstNonZero = combined.search(/[1-9]/);
    if (firstNonZero === -1)
        return { sign: 1, whole: '0', fraction: '' };
    const digits = combined.slice(firstNonZero);
    const decimalIndex = whole.length + exponent - firstNonZero;
    if (decimalIndex <= 0) {
        return { sign, whole: '0', fraction: `${'0'.repeat(-decimalIndex)}${digits}` };
    }
    if (decimalIndex >= digits.length) {
        return { sign, whole: `${digits}${'0'.repeat(decimalIndex - digits.length)}`, fraction: '' };
    }
    return { sign, whole: digits.slice(0, decimalIndex), fraction: digits.slice(decimalIndex) };
};
// PostgreSQL's numeric round(value, 2) rounds halfway values away from zero.
// Math.round does not reliably do that for values such as 1.005 because they
// are represented as binary floating-point values in JavaScript.
export const roundMoney = (value) => {
    const parts = decimalParts(value);
    if (!parts)
        return 0;
    let whole = parts.whole;
    let cents = `${parts.fraction}00`.slice(0, 2);
    if ((parts.fraction[2] || '0') >= '5') {
        if (cents === '99') {
            whole = incrementDecimalDigits(whole);
            cents = '00';
        }
        else {
            cents = incrementDecimalDigits(cents).padStart(2, '0');
        }
    }
    const rounded = Number(`${parts.sign < 0 ? '-' : ''}${whole}.${cents}`);
    return rounded === 0 ? 0 : rounded;
};
const isReferenceRecord = item => ['supplierRecordMode','supplier_record_mode','record_mode'].some(key => item?.[key] === 'reference') || Object.prototype.hasOwnProperty.call(item || {}, 'reference_amount');
export const validateCanonicalVisitItems = (items = []) => {
    if (!Array.isArray(items))
        return { code: 'INVALID_VISIT_ITEMS' };
    for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        if (!item || typeof item !== 'object')
            return { code: 'INVALID_VISIT_ITEM', index };
        const itemType = String(item.itemType ?? item.item_type ?? 'service').trim().toLowerCase();
        if (!isCanonicalVisitItemType(itemType))
            return { code: 'INVALID_ITEM_TYPE', index };
        if (isReferenceRecord(item))
            return { code: 'REFERENCE_IN_FINANCIAL_ITEMS_FORBIDDEN', index };
        const quantity = Number(item.quantity ?? item.qty ?? 1);
        if (!Number.isFinite(quantity) || quantity <= 0)
            return { code: 'INVALID_ITEM_QUANTITY', index };
        const price = Number(item.price ?? item.unitPrice ?? item.unit_price ?? 0);
        if (!Number.isFinite(price) || price < 0)
            return { code: 'INVALID_ITEM_PRICE', index };
    }
    return null;
};
export const canonicalVisitItemValidationMessage = (validation) => {
    switch (validation?.code) {
        case 'REFERENCE_IN_FINANCIAL_ITEMS_FORBIDDEN':
            return 'تعذر فصل التوثيق عن الشراء. أعد تحميل الصفحة وراجع تصنيف البند.';
        case 'INVALID_ITEM_TYPE':
            return 'نوع البند غير مدعوم. اختر خدمة أو قطعة أو موردًا.';
        case 'INVALID_ITEM_QUANTITY':
            return 'الكمية يجب أن تكون أكبر من صفر.';
        case 'INVALID_ITEM_PRICE':
            return 'السعر يجب أن يكون رقمًا موجبًا أو صفرًا.';
        default:
            return 'تعذر التحقق من بنود الزيارة.';
    }
};
export const isPaymentPending = (payment = {}) => {
    const statusValue = String(payment.status || payment.paymentStatus || payment.payment_status || '').trim().toLowerCase();
    return payment.confirmed === false || [
        'pending',
        'pending_confirmation',
        'awaiting_confirmation',
        'unconfirmed',
        'بانتظار التأكيد',
        'بانتظار_التأكيد',
    ].includes(statusValue);
};
export const isVisitFinalized = (visit = {}) => {
    const financialState = String(visit.financialState || visit.financial_state || '').toLowerCase();
    return financialState === 'finalized' || Boolean(visit.finalizedAt || visit.finalized_at);
};
export const resolveItemBillingType = (item = {}) => (item.itemType === 'supplier' || item.item_type === 'supplier' ||
    item.billingType === 'supplier' || item.billing_type === 'supplier'
    ? 'supplier'
    : 'workshop');
export const normalizeVisitItem = (item = {}, index = 0, visitId = '') => {
    const itemType = String(item.itemType ?? item.item_type ?? 'service').trim().toLowerCase() || 'service';
    // Preserve malformed historical data for display, but do not silently turn
    // it into a service on the next save.
    const quantity = asNumber(item.quantity ?? item.qty, 1);
    const price = asNumber(item.price ?? item.unitPrice ?? item.unit_price, 0);
    const storedTotal = item.total ?? item.lineTotal ?? item.line_total;
    const total = storedTotal === null || storedTotal === undefined
        ? roundMoney(quantity * price)
        : roundMoney(storedTotal);
    return {
        ...item,
        id: item.id || `item-${visitId}-${index}`,
        clientKey: item.clientKey || item.client_key || item.id || `item-${visitId}-${index}`,
        itemType,
        unsupportedItemType: isCanonicalVisitItemType(itemType) ? null : itemType,
        serviceId: item.serviceId || item.service_id || null,
        partId: item.partId || item.part_id || null,
        supplierId: item.supplierId || item.supplier_id || null,
        name: item.name || '',
        details: item.details || '',
        quantity,
        qty: quantity,
        price,
        total,
        discount: asNumber(item.discount, 0),
        customerBillable: item.customerBillable ?? item.customer_billable ?? true,
        supplierSettlementStatus: item.supplierSettlementStatus || item.supplier_settlement_status || null,
        supplierPaymentMethod: item.supplierPaymentMethod || item.supplier_payment_method || null,
        effectRevision: item.effectRevision ?? item.effect_revision ?? 1,
        metadata: item.metadata || {},
        _priceQtyMismatch: price > 0 && Math.abs(total - (quantity * price)) > 0.5,
    };
};
export const normalizeVisitPayment = (payment = {}, index = 0, visitId = '') => ({
    ...payment,
    id: payment.id || `payment-${visitId}-${index}`,
    amount: roundMoney(payment.amount),
    method: payment.method || payment.paymentMethod || payment.payment_method || 'cash',
    paymentMethod: payment.paymentMethod || payment.payment_method || payment.method || 'cash',
    date: payment.date || payment.paymentDate || payment.payment_date || payment.createdAt || payment.created_at || '',
    reference: payment.reference || '',
    arApplied: roundMoney(payment.arApplied ?? payment.ar_applied),
    unappliedAdvance: roundMoney(payment.unappliedAdvance ?? payment.unapplied_advance),
    financialEventId: payment.financialEventId || payment.financial_event_id || null,
    confirmed: payment.confirmed !== false,
});
const plainVisitNotes = (visit = {}) => {
    const canonical = visit.notesText ?? visit.notes_text;
    if (canonical !== null && canonical !== undefined)
        return String(canonical);
    // Read-only compatibility for genuinely plain historical notes.
    // JSON notes are intentionally NOT parsed for items/payments/finalization.
    const legacy = visit.notes;
    if (typeof legacy !== 'string')
        return '';
    const trimmed = legacy.trim();
    if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('['))
        return '';
    return legacy;
};
export const normalizeVisitCardState = (visit = {}) => ({
    items: (Array.isArray(visit.items) ? visit.items : Array.isArray(visit.visit_items) ? visit.visit_items : [])
        .map((item, index) => normalizeVisitItem(item, index, visit.id)),
    payments: (Array.isArray(visit.payments) ? visit.payments : Array.isArray(visit.visit_payments) ? visit.visit_payments : [])
        .map((payment, index) => normalizeVisitPayment(payment, index, visit.id)),
    status: visit.status || 'in_progress',
    technicianId: visit.technicianId || visit.technician_id || '',
    mileage: visit.mileage ?? '',
    notes: plainVisitNotes(visit),
});
export const buildCanonicalVisitItems = (items = []) => {
    if (items.some(item => isReferenceRecord(item))) throw new Error('REFERENCE_IN_FINANCIAL_ITEMS_FORBIDDEN');
    return items.map((item, index) => ({
    ...((item.itemType ?? item.item_type) === 'supplier' ? {supplierRecordMode:'purchase'} : {}),
    id: item.id || undefined,
    clientKey: item.clientKey || item.client_key || item.id || `item-${index}`,
    itemType: String(item.itemType ?? item.item_type ?? 'service').trim().toLowerCase() || 'service',
    serviceId: item.serviceId || item.service_id || null,
    partId: item.partId || item.part_id || null,
    supplierId: item.supplierId || item.supplier_id || null,
    name: String(item.name || '').trim(),
    quantity: asNumber(item.quantity ?? item.qty, 1),
    price: roundMoney(item.price ?? item.unitPrice ?? item.unit_price),
    customerBillable: item.customerBillable ?? item.customer_billable ?? true,
    supplierSettlementStatus: item.supplierSettlementStatus || item.supplier_settlement_status || null,
    supplierPaymentMethod: item.supplierPaymentMethod || item.supplier_payment_method || null,
}));
};
export const customerBillableTotal = (items = []) => roundMoney(items.reduce((sum, item) => {
    const billable = item.customerBillable ?? item.customer_billable ?? true;
    if (!billable)
        return sum;
    const lineTotal = item.total ?? roundMoney(asNumber(item.quantity ?? item.qty, 1) * asNumber(item.price, 0));
    // visit_items.line_total is rounded per line by PostgreSQL before totals are summed.
    return sum + roundMoney(lineTotal);
}, 0));
export const confirmedPaymentsTotal = (payments = []) => roundMoney(payments
    .filter((payment) => !isPaymentPending(payment))
    .reduce((sum, payment) => sum + asNumber(payment.amount, 0), 0));
export const arAppliedTotal = (payments = []) => roundMoney(payments.reduce((sum, payment) => sum + asNumber(payment.arApplied ?? payment.ar_applied, 0), 0));
export const unappliedAdvanceTotal = (payments = []) => roundMoney(payments.reduce((sum, payment) => sum + asNumber(payment.unappliedAdvance ?? payment.unapplied_advance, 0), 0));
export const visitDisplayTotal = (visit = {}, items = []) => {
    const finalized = isVisitFinalized(visit);
    const finalTotal = asNumber(visit.finalCustomerTotal ?? visit.final_customer_total, NaN);
    if (finalized && Number.isFinite(finalTotal))
        return roundMoney(finalTotal);
    return customerBillableTotal(items);
};
export const visitRemainingBalance = (visit = {}, items = [], payments = []) => {
    const explicitBalance = asNumber(visit.balance ?? visit.displayRemaining ?? visit.display_remaining, NaN);
    if (Number.isFinite(explicitBalance))
        return Math.max(0, roundMoney(explicitBalance));
    const total = visitDisplayTotal(visit, items);
    if (isVisitFinalized(visit)) {
        return Math.max(0, roundMoney(total - arAppliedTotal(payments)));
    }
    // Before finalization there is no accounts receivable. Advances are liabilities, not AR settlement.
    return 0;
};
export const createRequestKey = (prefix, visitId) => {
    const uuid = typeof window !== 'undefined' ? window.crypto?.randomUUID?.() : undefined;
    const random = uuid || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}:${visitId}:${random}`;
};
